/* eslint-disable @typescript-eslint/ban-ts-comment */
//@ts-nocheck

/**
 * CONTRACT ADDRESS CONFIGURATION - SECURITY & MAINTENANCE GUIDE
 *
 * IMPORTANT: Never commit real mainnet contract addresses to version control.
 * Use environment variables to configure contract addresses per environment.
 *
 * SETUP INSTRUCTIONS:
 * ===================
 *
 * 1. Copy .env-cmdrc.example to .env-cmdrc (if not exists)
 * 2. Set appropriate contract addresses for each environment
 * 3. For production, use mainnet contract addresses
 * 4. For staging/dev, use testnet contract addresses
 * 5. For local development, use local deployment addresses
 *
 * ENVIRONMENT VARIABLES:
 * ======================
 * REACT_APP_TOKEN_CONTRACT_ADDRESS                    - Main token contract
 * REACT_APP_IDENTITY_VERIFICATION_CONTRACT_ADDRESS    - KYC verification contract
 * REACT_APP_COMPLIANCE_REGISTRY_CONTRACT_ADDRESS      - Compliance registry contract
 * REACT_APP_RESELL_POOL_CONTRACT_ADDRESS              - Resell pool contract
 * REACT_APP_PROPERTY_ASSET_CONTRACT_ADDRESS           - Property asset implementation
 * REACT_APP_PROPERTY_FACTORY_CONTRACT_ADDRESS         - Property factory contract
 * REACT_APP_TOKEN_REGISTRY_CONTRACT_ADDRESS           - Token registry contract
 * REACT_APP_RENTAL_POOL_CONTRACT_ADDRESS              - Rental pool contract
 * REACT_APP_NETWORK_ID                                - Network ID (1=mainnet, 56=BSC mainnet, 97=BSC testnet)
 *
 * Note: Payment token addresses (USDT, USDC, DAI, ETH) are configured separately
 * in src/contractData/tokenAddresses/index.ts for better organization and network support.
 *
 * SECURITY NOTES:
 * ===============
 * - Contract addresses should be verified against official deployments
 * - Use checksum addresses (mixed case) for better validation
 * - Regularly audit and update addresses when contracts are redeployed
 * - Consider using a contract registry or ENS for dynamic address resolution
 */

/**
 * CONTRACT ADDRESS CONFIGURATION
 *
 * Security & Maintenance Best Practices:
 * - Contract addresses are now configurable via environment variables
 * - Supports multiple networks (mainnet, testnet, local development)
 * - Fallbacks to BSC Testnet defaults for backward compatibility
 * - Validates required addresses and provides clear error messages
 *
 * Environment Variables:
 * - REACT_APP_TOKEN_CONTRACT_ADDRESS
 * - REACT_APP_IDENTITY_VERIFICATION_CONTRACT_ADDRESS
 * - REACT_APP_COMPLIANCE_REGISTRY_CONTRACT_ADDRESS
 * - REACT_APP_RESELL_POOL_CONTRACT_ADDRESS
 * - REACT_APP_PROPERTY_ASSET_CONTRACT_ADDRESS
 * - REACT_APP_PROPERTY_FACTORY_CONTRACT_ADDRESS
 * - REACT_APP_TOKEN_REGISTRY_CONTRACT_ADDRESS
 * - REACT_APP_RENTAL_POOL_CONTRACT_ADDRESS
 *
 * Network Detection:
 * - Uses REACT_APP_NETWORK_ID to determine network (defaults to 97 - BSC Testnet)
 */

function getContractAddresses() {
  // Network configuration
  const networkId = parseInt(process.env.REACT_APP_NETWORK_ID || '97', 10);
  const networkName =
    networkId === 1 ? 'mainnet' : networkId === 97 ? 'testnet' : `network-${networkId}`;

  console.log(`Loading contract addresses for ${networkName} (Network ID: ${networkId})`);

  // BSC Testnet defaults (fallback values)
  const BSC_TESTNET_DEFAULTS = {
    token: '0x341218Eb704b45e72FFB73724E33a91F66f799dF',
    identityVerification: '0x43570f1e8315E5C5d7a90A129a5044180c338A4D',
    complianceRegistry: '0x07115f5e97e3218328587Bb8bd6dFf452DCAAE8E',
    resellPool: '0x8F99e3825b2dc1A337D2208058e8a9F8717308fa',
    propertyAssetImplimentation: '0x9EC5E94758355944F4f414A33fF7385cD726B7C5',
    propertyFactory: '0xDA24c738F5ef3f34bD4E68Cb927C8423097bFeB6',
    tokenRegistry: '0xbDACC3d59E84A7abf59F0531Ee2ECeA702B7a095',
    rentalPool: '0x39B4e866d960adF903A203aC07a652335af2B900',
  };

  // Load addresses from environment variables with fallbacks
  const contracts = {
    token: process.env.REACT_APP_TOKEN_CONTRACT_ADDRESS || BSC_TESTNET_DEFAULTS.token,
    identityVerification:
      process.env.REACT_APP_IDENTITY_VERIFICATION_CONTRACT_ADDRESS ||
      BSC_TESTNET_DEFAULTS.identityVerification,
    complianceRegistry:
      process.env.REACT_APP_COMPLIANCE_REGISTRY_CONTRACT_ADDRESS ||
      BSC_TESTNET_DEFAULTS.complianceRegistry,
    resellPool:
      process.env.REACT_APP_RESELL_POOL_CONTRACT_ADDRESS || BSC_TESTNET_DEFAULTS.resellPool,
    propertyAssetImplimentation:
      process.env.REACT_APP_PROPERTY_ASSET_CONTRACT_ADDRESS ||
      BSC_TESTNET_DEFAULTS.propertyAssetImplimentation,
    propertyFactory:
      process.env.REACT_APP_PROPERTY_FACTORY_CONTRACT_ADDRESS ||
      BSC_TESTNET_DEFAULTS.propertyFactory,
    tokenRegistry:
      process.env.REACT_APP_TOKEN_REGISTRY_CONTRACT_ADDRESS || BSC_TESTNET_DEFAULTS.tokenRegistry,
    rentalPool:
      process.env.REACT_APP_RENTAL_POOL_CONTRACT_ADDRESS || BSC_TESTNET_DEFAULTS.rentalPool,
  };

  // Validate critical contract addresses
  const criticalContracts = [
    'token',
    'identityVerification',
    'complianceRegistry',
    'propertyFactory',
  ];
  const missingContracts = criticalContracts.filter(
    (key) => !contracts[key as keyof typeof contracts]
  );

  if (missingContracts.length > 0) {
    console.error('Missing critical contract addresses:', missingContracts);
    throw new Error(`Critical contract addresses not configured: ${missingContracts.join(', ')}`);
  }

  // Log loaded addresses for debugging (in development only)
  if (process.env.NODE_ENV === 'development') {
    console.log('Contract addresses loaded:', {
      network: networkName,
      addresses: contracts,
    });
  }

  return contracts;
}
export default getContractAddresses;
